/**
 * One selection across a library holding two kinds of thing, and one record of what is open.
 *
 * A person picking in this column does not think of a photograph and a typeface as two selections,
 * so there is one set here and the kind travels in the key: `media:<id>`, `font:<id>`. Two sets
 * would have to be kept in step by every caller -- the count in the bulk bar, the clear, the reset
 * after a delete -- and the first one somebody forgot would leave a bar claiming things nobody can
 * see selected.
 *
 * Selecting and opening are separate questions asked by separate controls: a checkbox says what a
 * bulk action will hit, a click on the card body says what the detail panel is describing. They are
 * keyed the same way so the host can hold "open" as one key beside the set of selected ones, and so
 * a card can be both at once without the two states having to agree.
 *
 * Prefixed rather than trusted to be distinct. Both ids are uuids today, but a selection that only
 * works because two id spaces happen not to collide is one migration away from filing a font into
 * an asset's folder.
 */

/* eslint-disable i18next/no-literal-string -- internal key prefixes, never displayed */
const MEDIA_PREFIX = 'media:';
const FONT_PREFIX = 'font:';
/* eslint-enable i18next/no-literal-string */

export const mediaKey = (mediaId: string): string => `${MEDIA_PREFIX}${mediaId}`;
export const fontKey = (fontId: string): string => `${FONT_PREFIX}${fontId}`;

/**
 * Adds a key that is not there, removes one that is.
 *
 * The only thing a checkbox can do, so there is no modified-click variant to carry: ticking a
 * second box adds to the selection rather than replacing it, which is what makes multi-select work
 * without a keyboard.
 *
 * Returns a new Set rather than mutating, so a caller can hand it straight to setState and React
 * sees a change.
 */
export const toggleSelected = (keys: ReadonlySet<string>, key: string): Set<string> => {
  const next = new Set(keys);
  if (!next.delete(key)) next.add(key);
  return next;
};

/**
 * Whether every one of `keys` is already selected.
 *
 * An empty `keys` is not "all": a control asking about a section holding nothing would otherwise
 * offer to deselect what is not there.
 */
export const allSelected = (selection: ReadonlySet<string>, keys: readonly string[]): boolean =>
  keys.length > 0 && keys.every((key) => selection.has(key));

/**
 * Adds every key while any is missing, and takes them all back out once they are all there.
 *
 * Only these keys move. A control standing over one part of the library -- one section, or what the
 * filters have left on screen -- must not empty a choice it never showed, so what is selected
 * elsewhere is carried through untouched.
 */
export const toggleAll = (selection: ReadonlySet<string>, keys: readonly string[]): Set<string> => {
  const next = new Set(selection);
  if (allSelected(selection, keys)) keys.forEach((key) => next.delete(key));
  else keys.forEach((key) => next.add(key));
  return next;
};

const idsWithPrefix = (keys: ReadonlySet<string>, prefix: string): string[] =>
  [...keys].filter((key) => key.startsWith(prefix)).map((key) => key.slice(prefix.length));

/** The media half, as bare ids, for a call that only speaks about assets. */
export const selectedMediaIds = (keys: ReadonlySet<string>): string[] =>
  idsWithPrefix(keys, MEDIA_PREFIX);

export const selectedFontIds = (keys: ReadonlySet<string>): string[] =>
  idsWithPrefix(keys, FONT_PREFIX);

/**
 * The same halves as sets, for the grids.
 *
 * A grid is told about its own kind only: `MediaGrid` should not have to know that the key it is
 * being asked about was ever qualified, and giving it the qualified set would make every tile miss.
 */
export const selectedMediaIdSet = (keys: ReadonlySet<string>): ReadonlySet<string> =>
  new Set(selectedMediaIds(keys));

export const selectedFontIdSet = (keys: ReadonlySet<string>): ReadonlySet<string> =>
  new Set(selectedFontIds(keys));

const idWithPrefix = (key: string | null, prefix: string): string | undefined =>
  key !== null && key.startsWith(prefix) ? key.slice(prefix.length) : undefined;

/** The media a single key names, or undefined when it names a font or nothing is open. */
export const mediaIdIn = (key: string | null): string | undefined =>
  idWithPrefix(key, MEDIA_PREFIX);

/** The font a single key names, or undefined when it names an asset or nothing is open. */
export const fontIdIn = (key: string | null): string | undefined => idWithPrefix(key, FONT_PREFIX);
